/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { TmApiError } from '@treasury/domain/shared';
import {
    CreditorsService,
    WireBeneficiary,
    WireCreditor,
    WireCreditorFilter,
} from '@treasury/domain/wires';
import { TmContainer, TmFooterService } from '@treasury/presentation';
import { circlePlusIcon } from '@treasury/presentation/assets/icons';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import '../../partials/wires-container-sidebar';
import '../../partials/wires-container-tabs';
import '../partials/creditors-table';

export const tagName = 'creditors-container';
@customElement(tagName)
export class CreditorsContainer extends TmContainer {
    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly footerService: TmFooterService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @state()
    private domesticCreateCreditorEntitlement = false;

    @state()
    private internationalCreateCreditorEntitlement = false;

    @state()
    private creditors: Array<WireBeneficiary> | Array<WireCreditor> = [];

    @state()
    private selectedCreditors: Array<WireBeneficiary> | Array<WireCreditor> = [];

    @state()
    private loadingCreditors = false;

    private get hasCreateCreditorEntitlement() {
        return (
            this.domesticCreateCreditorEntitlement || this.internationalCreateCreditorEntitlement
        );
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.loading = true;
        this.loadingCreditors = true;
    }

    protected render() {
        if (this.loading && !this.isDesktop) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return html`
            <tm-sidebar>
                <div
                    class=${classMap({
                        'flex-grow flex flex-col h-full': true,
                        'py-2': this.isDesktop,
                    })}
                    slot="main-content"
                >
                    <div class="flex-none">
                        ${this.isDesktop ? nothing : this.renderActionItems()}
                    </div>
                    <div class="flex-grow">
                        <tm-section
                            class=${classMap({
                                'flex flex-col h-full': true,
                                'overflow-hidden': !this.isDesktop,
                                'mx-2 pb-2 rounded-lg': this.isDesktop,
                            })}
                        >
                            <div class="flex flex-col h-full">
                                ${this.isDesktop
                                    ? html`<div
                                          class=${classMap({
                                              'flex justify-between items-center border-b': true,
                                          })}
                                      >
                                          <wires-container-tabs
                                              .activeTab=${`Creditors`}
                                              @switchTab=${({ detail }: CustomEvent) => {
                                                  switch (detail.activeTab) {
                                                      case 'OneTime':
                                                      case 'Recurring':
                                                      case 'WireFile':
                                                      case 'WireFileFormats':
                                                          this.navService.navigate('/wires', {
                                                              tab: detail.activeTab,
                                                          });
                                                          break;
                                                      case 'Templates':
                                                          this.navService.navigate(
                                                              '/wires/templates/view'
                                                          );
                                                          break;
                                                      default:
                                                          break;
                                                  }
                                              }}
                                          ></wires-container-tabs>
                                          ${this.hasCreateCreditorEntitlement
                                              ? html`<tm-button
                                                    importance="primary"
                                                    .icon=${circlePlusIcon}
                                                    class="mr-4"
                                                    @click=${() => {
                                                        this.navService.navigate(
                                                            `/wires/creditors/new/`
                                                        );
                                                    }}
                                                    >${`Create creditor`}</tm-button
                                                >`
                                              : nothing}
                                      </div>`
                                    : nothing}
                                <div class="flex-grow">
                                    <creditors-table
                                        class="h-full"
                                        .creditors=${this.creditors}
                                        .creditorDetail=${true}
                                        .loading=${this.loadingCreditors}
                                        @selection-changed=${(e: CustomEvent) => {
                                            this.selectedCreditors = e.detail;
                                        }}
                                        @applyFilters=${(e: CustomEvent) => {
                                            this.fetchCreditors(e.detail);
                                        }}
                                    ></creditors-table>
                                </div>
                            </div>
                        </tm-section>
                    </div>
                </div>
                <wires-container-sidebar slot="sidebar-content"></wires-container-sidebar>
            </tm-sidebar>
            ${this.configureFooter()}
        `;
    }

    protected async firstUpdated() {
        try {
            this.domesticCreateCreditorEntitlement = await this.entitlementsService.isEnabled(
                Entitlements.CreateDomesticWire
            );
            this.internationalCreateCreditorEntitlement = await this.entitlementsService.isEnabled(
                Entitlements.CreateInternationalWire
            );
        } catch {
            this.notificationService.renderError('Failed to load feature flags');
        } finally {
            this.loading = false;
        }

        this.configureHeader();
    }

    protected async fetchCreditors(filters: WireCreditorFilter) {
        this.loadingCreditors = true;
        try {
            const creditors = await this.creditorsService.getAll(filters);
            this.creditors = creditors;
            this.configureHeader();
        } catch (error) {
            this.notificationService.renderError(error as unknown as TmApiError);
        } finally {
            this.loadingCreditors = false;
        }
    }

    private renderActionItems() {
        if (this.hasCreateCreditorEntitlement) {
            return html`<tm-action-button
                class="my-2"
                .disabled=${false}
                .buttonTitle=${`Create creditor`}
                @click=${() => {
                    this.navService.navigate(`/wires/creditors/new/`);
                }}
            ></tm-action-button>`;
        }
        return nothing;
    }

    private configureHeader() {
        this.headerService.configure({
            title: 'Creditors',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.WireCreditors}
                        .disabled=${!this.creditors.length}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
            backAction: () => this.navService.navigate('/wires'),
        });
    }

    private configureFooter() {
        if (!this.isDesktop) {
            this.footerService.hide();
            return;
        }
        this.footerService.configure({
            direction: 'row',
            buttonConfig: [
                {
                    text: 'Initiate payments',
                    disabled: this.selectedCreditors.length === 0,
                    onClick: () => {
                        this.navService.navigate(
                            `/wires/${this.selectedCreditors[0]?.isInternational ? 'international' : 'domestic'}/create/${this.selectedCreditors[0]?.id}/usd`
                        );
                    },
                },
            ],
        });
    }

    public static get styles() {
        return [
            css`
                :host {
                    height: 100%;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CreditorsContainer;
    }
}
